import _ from 'lodash';
import React, { createContext } from 'react';
import { FieldValues } from 'react-hook-form';
import { ProductConfigurationResponse } from '../api/ConfigApi';
import EndorsementsApi from '../api/EndorsementsApi';
import { handleUnauthorizedResponse } from '../api/http';
import PolicyApi from '../api/PolicyApi';
import ProductApi from '../api/ProductApi';
import { FORM_NAME } from '../components/fastForm/data/constants';
import {
	getEndorsementExposuresForm,
	getEndorsementsForm,
} from '../components/fastForm/formBuilders';
import {
	FormBuilder,
	FormShape,
	SaveFastForm,
	SaveOptions,
} from '../components/fastForm/types';
import endorsementRenewalUpdateBuilder from '../components/fastForm/updateBuilders/endorsementRenewalUpdateBuilder';
import {
	getHasInputRepriceBehavior,
	getHasPaymentPluginEnabled,
} from '../components/policy/util_sharedEndorsementAndRenewal';
import { successToast } from '../components/Toast';
import {
	ENDORSEMENT_ACCEPT_FAILURE,
	ENDORSEMENT_ACCEPT_SUCCESS,
	ENDORSEMENT_DISCARD_FAILURE,
	ENDORSEMENT_DISCARD_SUCCESS,
	ENDORSEMENT_INVALIDATE_FAILURE,
	ENDORSEMENT_INVALIDATE_SUCCESS,
	ENDORSEMENT_ISSUE_FAILURE,
	ENDORSEMENT_ISSUE_SUCCESS,
	ENDORSEMENT_QUOTE_FAILURE,
	ENDORSEMENT_QUOTE_SUCCESS,
	ENDORSEMENT_UPDATE_FAILURE,
	ENDORSEMENT_UPDATE_SUCCESS,
	FETCH_ENDORSEMENT_FAILURE,
	LOAD_ENDORSEMENT_FAILURE,
} from '../data/labelsAndMessages';
import {
	EndorsementActionRequest,
	EndorsementResponse,
	EndorsementUpdateRequest,
} from '../types/endorsements';
import {
	Exposure,
	ExposureCreateRequest,
	FieldGroupUpdateRequest,
	FieldValuesMap,
	Policy2,
} from '../types/policy';
import { getPolicyConfigVersion, isEmpty } from '../util/util';
import {
	AuthContext,
	AuthData,
	ENDORSEMENT_POLICY_END_DATE_ENABLED,
	HIDE_PERIL_LIMITS_ENABLE,
} from './auth';
import { ErrorContext, ErrorData } from './ErrorProvider';

export const EndorsementContext = createContext<EndorsementData>(null);

export const useEndorsementContext = () => React.useContext(EndorsementContext);

export function withEndorsementContext<T>(Component: any) {
	return (props: Omit<T, 'errorData' | 'endorsementData' | 'authData'>) => (
		<ErrorContext.Consumer>
			{(errorData) => (
				<EndorsementContext.Consumer>
					{(endorsementData) => (
						<AuthContext.Consumer>
							{(authData) => (
								<Component
									{...props}
									authData={authData}
									endorsementData={endorsementData}
									errorData={errorData}
								/>
							)}
						</AuthContext.Consumer>
					)}
				</EndorsementContext.Consumer>
			)}
		</ErrorContext.Consumer>
	);
}

export interface EndorsementData {
	endorsementResponse: EndorsementResponse;
	endorsementUpdateObj: EndorsementResponse;
	policy: Policy2;
	productConfig: ProductConfigurationResponse;
	selectedExposure?: Exposure | ExposureCreateRequest;
	setLoading?: (loading: boolean) => void;
	setEndorsementUpdateObj: (
		endorsementUpdateObj: EndorsementUpdateRequest,
	) => Promise<EndorsementData>;
	setEndorsementResponse: (
		endorsementResponse: EndorsementResponse,
	) => Promise<EndorsementData>;
	setSelectedExposure: (
		selectedExposure: Exposure | ExposureCreateRequest,
	) => Promise<EndorsementData>;
	updateEndorsement: () => void;
	quoteEndorsement: SaveFastForm<boolean>;
	acceptEndorsement: SaveFastForm<boolean>;
	issueEndorsement: SaveFastForm<boolean>;
	invalidateEndorsement: () => void;
	discardEndorsement: () => void;
	updateGroupFieldValueForEndorsement: (
		name: string,
		values: FieldValuesMap,
		index?: number,
		fieldGroupLocator?: string,
	) => any;
	loadData: (
		policyId: string,
		endorsementId: string,
	) => Promise<EndorsementData> | null;
	setPolicy: (policy: Policy2) => Promise<EndorsementData>;
	fields: FormShape<FormBuilder>;
	updateFields?: () => Promise<EndorsementData>;
	saveFastForm: SaveFastForm<EndorsementResponse>;
	getFastFormEndorsementUpdateObj: (
		values: FieldValues,
		options: SaveOptions,
		dirtyFields: {
			[key: string]: boolean;
		},
	) => EndorsementUpdateRequest;
	cloneEndorsement: () => Promise<EndorsementResponse>;
}

interface Props {
	children: any;
	authData: AuthData;
	errorData: ErrorData;
}

export class EndorsementProvider extends React.Component<
	Props,
	EndorsementData
> {
	constructor(props: any, context: any) {
		super(props);
		this.state = {
			endorsementResponse: null,
			endorsementUpdateObj: null,
			policy: null,
			productConfig: null,
			setEndorsementUpdateObj: this.setEndorsementUpdateObj,
			setEndorsementResponse: this.setEndorsementResponse,
			setSelectedExposure: this.setSelectedExposure,
			updateEndorsement: this.updateEndorsement,
			quoteEndorsement: this.quoteEndorsement,
			acceptEndorsement: this.acceptEndorsement,
			issueEndorsement: this.issueEndorsement,
			invalidateEndorsement: this.invalidateEndorsement,
			discardEndorsement: this.discardEndorsement,
			updateGroupFieldValueForEndorsement:
				this.updateGroupFieldValueForEndorsement,
			loadData: this.loadData,
			setPolicy: this.setPolicy,
			fields: null,
			updateFields: this.updateFields,
			saveFastForm: this.saveFastForm,
			getFastFormEndorsementUpdateObj: this.getFastFormEndorsementUpdateObj,
			cloneEndorsement: this.cloneEndorsement,
		};
	}

	public render() {
		return (
			<EndorsementContext.Provider value={this.state}>
				{this.props.children}
			</EndorsementContext.Provider>
		);
	}

	private updateFields = () => {
		const fields = {
			...getEndorsementsForm({
				endorsementData: this.state,
				authData: this.props.authData,
			}),
			...getEndorsementExposuresForm({
				endorsementData: this.state,
				authData: this.props.authData,
			}),
		};
		const promise = new Promise<EndorsementData>((resolve) => {
			this.setState({ fields }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private getFastFormEndorsementUpdateObj = (
		values: FieldValues,
		options: SaveOptions,
		dirtyFields: {
			[key: string]: boolean;
		},
	) => {
		const { endorsementUpdateObj, fields } = this.state;
		const { authData } = this.props;
		const hidePerilLimitsEnabled =
			authData?.auth?.configProperties?.[HIDE_PERIL_LIMITS_ENABLE] ?? false;
		const {
			addFieldGroups,
			removeFieldGroups,
			updateFieldGroups,
			addExposures,
			endExposures,
			fieldValues,
			updateExposures,
		} = endorsementRenewalUpdateBuilder(
			dirtyFields,
			values,
			fields,
			FORM_NAME.ENDORSEMENT_FORM,
			{
				...options,
			},
			authData.auth.timezone,
			endorsementUpdateObj,
			hidePerilLimitsEnabled,
		);
		const isEndorsementEndDateEnabled =
			authData.auth.configProperties[ENDORSEMENT_POLICY_END_DATE_ENABLED];

		const updateObject: EndorsementUpdateRequest = {
			addFieldGroups,
			removeFieldGroups,
			updateFieldGroups,
			addExposures,
			endExposures,
			fieldValues,
			updateExposures,

			// not related to fast form
			startTimestamp: endorsementUpdateObj.startTimestamp,
			// Exclude newPolicyEndTimestamp from endorsement update requests unless the enabling flag is set
			// to avoid errors validation errors
			newPolicyEndTimestamp: !isEndorsementEndDateEnabled
				? null
				: endorsementUpdateObj.newPolicyEndTimestamp,
			newPaymentScheduleName: endorsementUpdateObj.newPaymentScheduleName,
		};

		// add reprice input if reprice behavior is 'input'
		if (this.checkKeepRepriceInput()) {
			updateObject.reprice =
				typeof endorsementUpdateObj.reprice !== 'undefined'
					? endorsementUpdateObj.reprice
					: endorsementUpdateObj.reprice;
		}

		if (options?.runAutofill) {
			// default specified autofill sequence will run when autofill array is null
			updateObject.autofill = null;
		} else {
			// for regular updates, send an empty array to signify to run no autofills
			updateObject.autofill = [];
		}

		return updateObject;
	};

	private saveFastForm: SaveFastForm<EndorsementResponse> = async (
		values,
		dirtyFields,
		reset,
		options,
	) => {
		let updatedEndorsementResponse: EndorsementResponse;
		const { endorsementUpdateObj } = this.state;
		const { authData, errorData } = this.props;

		const updateObject = this.getFastFormEndorsementUpdateObj(
			values,
			options,
			dirtyFields,
		);

		// start saving here:
		try {
			const endorsementResponse = await EndorsementsApi.updateEndorsement(
				authData.auth.authorizationToken,
				endorsementUpdateObj.locator,
				{ endorsementUpdate: updateObject },
			);
			const endorsementResponseData = endorsementResponse.data;
			await this.setEndorsementResponse(endorsementResponseData);
			await this.setEndorsementUpdateObj(_.cloneDeep(endorsementResponseData));
			await this.updateFields();
			updatedEndorsementResponse = endorsementResponseData;
		} catch (error) {
			errorData.handleErrorResponse(error, ENDORSEMENT_UPDATE_FAILURE());
			if (await handleUnauthorizedResponse(error, authData)) {
				updatedEndorsementResponse = {} as EndorsementResponse;
			}
			throw error;
		}

		return updatedEndorsementResponse;
	};

	private setEndorsementUpdateObj = (
		endorsementUpdateObj: EndorsementResponse,
	) => {
		const promise = new Promise<EndorsementData>((resolve) => {
			this.setState({ endorsementUpdateObj }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private setEndorsementResponse = (
		endorsementResponse: EndorsementResponse,
	) => {
		const promise = new Promise<EndorsementData>((resolve) => {
			this.setState({ endorsementResponse }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private setSelectedExposure = (selectedExposure: Exposure) => {
		const promise = new Promise<EndorsementData>((resolve) => {
			this.setState({ selectedExposure }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private setPolicy = (policy: Policy2) => {
		const promise = new Promise<EndorsementData>((resolve) => {
			this.setState({ policy }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private setProductConfig = (productConfig: ProductConfigurationResponse) => {
		const promise = new Promise<EndorsementData>((resolve, reject) => {
			this.setState({ productConfig }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private updateGroupFieldValueForEndorsement = (
		name: string,
		fieldGroupValues: FieldValuesMap,
		index?: number,
		fieldGroupLocator?: string,
	) => {
		const { endorsementUpdateObj } = this.state;

		if (fieldGroupLocator) {
			// updateFieldGroups
			const updateFieldGroups = endorsementUpdateObj.updateFieldGroups || [];
			let updateRequest = updateFieldGroups.find(
				(req) => req.fieldGroupLocator === fieldGroupLocator,
			);
			let updateIndex = updateFieldGroups.findIndex(
				(req) => req.fieldGroupLocator === fieldGroupLocator,
			);
			updateIndex = updateIndex === -1 ? 0 : updateIndex;

			const newUpdate = {
				fieldGroupLocator,
				fieldName: name,
				// fieldValues: fieldGroupValues,
			} as FieldGroupUpdateRequest;

			updateRequest = updateRequest || newUpdate;
			updateRequest.fieldValues = fieldGroupValues;

			// update field exists but locator doesn't match.
			if (updateRequest) {
				updateFieldGroups[updateIndex] = updateRequest;
			}

			endorsementUpdateObj.updateFieldGroups = updateFieldGroups;
			this.setEndorsementUpdateObj(endorsementUpdateObj);
		} else {
			// add field groups
			this.setEndorsementUpdateObj(endorsementUpdateObj);
		}
	};

	/**
	 * Loads policy and endorsement for EndorsementRoutes
	 * @param policyId
	 * @param endorsementId
	 * @returns Promise<EndorsementData>
	 */
	private loadData = async (
		policyId: string,
		endorsementId: string,
	): Promise<EndorsementData> | null => {
		const { authData, errorData } = this.props;
		if (!authData) {
			return null;
		}
		if (!authData.auth) {
			return null;
		}
		try {
			const policyResponse = await PolicyApi.getPolicy(
				authData.auth.authorizationToken,
				policyId,
			);
			const policy = policyResponse.data as Policy2;
			const policyVersion = getPolicyConfigVersion(policy);
			const pvEnabled =
				authData.auth.configProperties[
					'property.product.versioning.m2.enabled'
				] ||
				authData.auth.configProperties[
					'property.product.versioning.with.repair.enabled'
				];
			let configs = null;
			if (pvEnabled) {
				configs = await ProductApi.getProductsWithVersion(
					authData.auth.authorizationToken,
					policyVersion,
				);
			} else {
				configs = await ProductApi.getProducts(
					authData.auth.authorizationToken,
				);
			}
			const productConfig = configs.data.find(
				(config: ProductConfigurationResponse) =>
					config.name === policy.productName,
			);
			await this.setPolicy(policy);
			await this.setProductConfig(productConfig);
		} catch (error) {
			errorData.handleErrorResponse(error, 'Failed to fetch policy');
			await handleUnauthorizedResponse(error, authData);

			return null;
		}

		try {
			const endorsementResponse = await EndorsementsApi.getEndorsement(
				authData.auth.authorizationToken,
				endorsementId,
			);

			if (endorsementResponse) {
				const endorsement = endorsementResponse.data;
				await this.setEndorsementResponse(endorsement);
				await this.setEndorsementUpdateObj(_.cloneDeep(endorsement));
			} else {
				console.error(LOAD_ENDORSEMENT_FAILURE());

				return null;
			}
		} catch (error) {
			errorData.handleErrorResponse(error, FETCH_ENDORSEMENT_FAILURE());
			await handleUnauthorizedResponse(error, authData);

			return null;
		}

		return this.updateFields();
	};

	// TODO cleanup
	private updateEndorsement = async () => {
		const { endorsementUpdateObj, setLoading, setEndorsementUpdateObj } =
			this.state;
		const { authData, errorData } = this.props;
		setLoading(true);

		// send all cumulative updates
		const updateObj: EndorsementUpdateRequest = {
			addFieldGroups: endorsementUpdateObj.addFieldGroups,
			removeFieldGroups: endorsementUpdateObj.removeFieldGroups,
			updateFieldGroups: endorsementUpdateObj.updateFieldGroups,
			addExposures: endorsementUpdateObj.addExposures,
			endExposures: endorsementUpdateObj.endExposures,
			fieldValues: endorsementUpdateObj.fieldValues,
			updateExposures: endorsementUpdateObj.updateExposures,
			startTimestamp: endorsementUpdateObj.startTimestamp,
			newPolicyEndTimestamp: endorsementUpdateObj.newPolicyEndTimestamp,
			newPaymentScheduleName: endorsementUpdateObj.newPaymentScheduleName,
		};

		// add reprice input if reprice behavior is 'input'
		if (this.checkKeepRepriceInput()) {
			updateObj.reprice =
				typeof endorsementUpdateObj.reprice !== 'undefined'
					? endorsementUpdateObj.reprice
					: endorsementUpdateObj.reprice;
		}

		try {
			const endorsementResponse = await EndorsementsApi.updateEndorsement(
				authData.auth.authorizationToken,
				endorsementUpdateObj.locator,
				{ endorsementUpdate: updateObj },
			);
			successToast(ENDORSEMENT_UPDATE_SUCCESS());
			await setEndorsementUpdateObj(endorsementResponse.data);
			await setLoading(false);
		} catch (error) {
			errorData.handleErrorResponse(error, ENDORSEMENT_UPDATE_FAILURE());
			await setLoading(false);
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
			throw error;
		}
	};

	private quoteEndorsement: SaveFastForm<boolean> = async (
		values,
		dirtyFields,
		reset,
		options,
	) => {
		const {
			endorsementUpdateObj,
			setLoading,
			setEndorsementUpdateObj,
			setEndorsementResponse,
		} = this.state;
		const { authData, errorData } = this.props;

		setLoading(true);
		const endorsementUpdate = this.getFastFormEndorsementUpdateObj(
			values,
			options,
			dirtyFields,
		);
		const actionReq: EndorsementActionRequest = {
			conflictHandling: 'invalidate',
		};

		if (!isEmpty(endorsementUpdate)) {
			actionReq.endorsementUpdate = endorsementUpdate;
			await EndorsementsApi.updateEndorsement(
				authData.auth.authorizationToken,
				endorsementUpdateObj.locator,
				actionReq,
			);
		}

		let result = false;
		try {
			const quotedEndorsement = await EndorsementsApi.quoteEndorsement(
				authData.auth.authorizationToken,
				endorsementUpdateObj.locator,
			);
			await setEndorsementResponse(quotedEndorsement.data);
			await setEndorsementUpdateObj(quotedEndorsement.data);
			await this.updateFields();
			successToast(ENDORSEMENT_QUOTE_SUCCESS());
			result = true;
		} catch (error) {
			errorData.handleErrorResponse(error, ENDORSEMENT_QUOTE_FAILURE());
			if (await handleUnauthorizedResponse(error, authData)) {
				return result;
			}
			throw error;
		} finally {
			setLoading(false);
		}
		return result;
	};

	private acceptEndorsement: SaveFastForm<boolean> = async (
		values,
		dirtyFields,
		reset,
		options,
		isDraft?,
	) => {
		const {
			endorsementUpdateObj,
			setLoading,
			setEndorsementUpdateObj,
			setEndorsementResponse,
		} = this.state;
		const { authData, errorData } = this.props;

		setLoading(true);
		const actionReq: EndorsementActionRequest = {
			conflictHandling: 'invalidate',
		};

		if (isDraft) {
			const endorsementUpdate = this.getFastFormEndorsementUpdateObj(
				values,
				options,
				dirtyFields,
			);
			if (!isEmpty(endorsementUpdate)) {
				actionReq.endorsementUpdate = endorsementUpdate;
				await EndorsementsApi.updateEndorsement(
					authData.auth.authorizationToken,
					endorsementUpdateObj.locator,
					actionReq,
				);
			}
		}

		let result = false;
		try {
			const acceptedEndorsement = await EndorsementsApi.acceptEndorsement(
				authData.auth.authorizationToken,
				endorsementUpdateObj.locator,
			);
			await setEndorsementResponse(acceptedEndorsement.data);
			await setEndorsementUpdateObj(acceptedEndorsement.data);
			successToast(ENDORSEMENT_ACCEPT_SUCCESS());
			result = true;
		} catch (error) {
			errorData.handleErrorResponse(error, ENDORSEMENT_ACCEPT_FAILURE());
			if (await handleUnauthorizedResponse(error, authData)) {
				return result;
			}
			throw error;
		} finally {
			setLoading(false);
		}
		return result;
	};

	private issueEndorsement: SaveFastForm<boolean> = async (
		values,
		dirtyFields,
		reset,
		options,
		isDraft?,
	) => {
		const {
			endorsementUpdateObj,
			setLoading,
			setEndorsementUpdateObj,
			setEndorsementResponse,
		} = this.state;
		const { authData, errorData } = this.props;
		setLoading(true);
		const actionReq: EndorsementActionRequest = {
			conflictHandling: 'invalidate',
		};

		if (isDraft) {
			const endorsementUpdate = this.getFastFormEndorsementUpdateObj(
				values,
				options,
				dirtyFields,
			);
			if (!isEmpty(endorsementUpdate)) {
				actionReq.endorsementUpdate = endorsementUpdate;
				await EndorsementsApi.updateEndorsement(
					authData.auth.authorizationToken,
					endorsementUpdateObj.locator,
					actionReq,
				);
			}
		}

		let result = false;
		try {
			const issuedEndorsement = await EndorsementsApi.issueEndorsement(
				authData.auth.authorizationToken,
				endorsementUpdateObj.locator,
			);
			await setEndorsementResponse(issuedEndorsement.data);
			await setEndorsementUpdateObj(issuedEndorsement.data);
			successToast(ENDORSEMENT_ISSUE_SUCCESS());
			result = true;
		} catch (error) {
			errorData.handleErrorResponse(error, ENDORSEMENT_ISSUE_FAILURE());
			if (await handleUnauthorizedResponse(error, authData)) {
				return result;
			}
			throw error;
		}
		setLoading(false);
		return result;
	};

	private cloneEndorsement = async () => {
		// Note: use `endorsementResponse` instead of `endorsementUpdateObj` since `endorsementResponse` holds the exact version of endorsement from server.
		// `endorsementUpdateObj` might hold "dirty" values and not getting saved to server yet.
		const { policy, productConfig, endorsementResponse } = this.state;
		const { authData } = this.props;

		const reprice = getHasInputRepriceBehavior(
			productConfig,
			endorsementResponse,
		)
			? {
					reprice: endorsementResponse.reprice,
			  }
			: {};

		// Note: newPaymentScheduleName is part of policy. Only copy it over when there is a value of it
		// in `endorsementResponse`; otherwise, it will result in copying "undefined" value over.
		const newPaymentScheduleName =
			getHasPaymentPluginEnabled(productConfig) &&
			'newPaymentScheduleName' in endorsementResponse
				? {
						newPaymentScheduleName: endorsementResponse.newPaymentScheduleName,
				  }
				: {};

		const endorsementCloneParams = {
			endorsementName: endorsementResponse.endorsementName,
			startTimestamp: endorsementResponse.startTimestamp,
			fieldValues: endorsementResponse.fieldValues,
			addFieldGroups: endorsementResponse.addFieldGroups,
			updateFieldGroups: endorsementResponse.updateFieldGroups,
			removeFieldGroups: endorsementResponse.removeFieldGroups,
			addExposures: endorsementResponse.addExposures,
			updateExposures: endorsementResponse.updateExposures,
			endExposures: endorsementResponse.endExposures,
			...reprice,
			...newPaymentScheduleName,
		};
		try {
			const clonedEndorsementResponse = await EndorsementsApi.createEndorsement(
				authData.auth.authorizationToken,
				policy.locator,
				endorsementCloneParams,
			);

			try {
				// attempt to price endorsement
				await EndorsementsApi.previewEndorsementPrice(
					authData.auth.authorizationToken,
					clonedEndorsementResponse.data.locator,
				);
			} catch (e) {
				// discard the endorsement
				await EndorsementsApi.discardEndorsement(
					authData.auth.authorizationToken,
					clonedEndorsementResponse.data.locator,
				);
				throw e;
			}

			await this.setEndorsementResponse(clonedEndorsementResponse.data);
			await this.setEndorsementUpdateObj(
				_.cloneDeep(clonedEndorsementResponse.data),
			);
			await this.updateFields();

			return clonedEndorsementResponse.data;
		} catch (e) {
			throw e;
		}
	};

	private invalidateEndorsement = async () => {
		const { endorsementUpdateObj, setLoading } = this.state;
		const { authData, errorData } = this.props;
		setLoading(true);

		try {
			await EndorsementsApi.invalidateEndorsement(
				authData.auth.authorizationToken,
				endorsementUpdateObj.locator,
				{ conflictHandling: 'invalidate' },
			);
			successToast(ENDORSEMENT_INVALIDATE_SUCCESS());
		} catch (error) {
			errorData.handleErrorResponse(error, ENDORSEMENT_INVALIDATE_FAILURE());
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
			throw error;
		}
		setLoading(false);
	};

	private discardEndorsement = async () => {
		const { endorsementUpdateObj, setLoading } = this.state;
		const { authData, errorData } = this.props;
		setLoading(true);
		try {
			await EndorsementsApi.discardEndorsement(
				authData.auth.authorizationToken,
				endorsementUpdateObj.locator,
			);
			successToast(ENDORSEMENT_DISCARD_SUCCESS());
		} catch (error) {
			errorData.handleErrorResponse(error, ENDORSEMENT_DISCARD_FAILURE());
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
			throw error;
		}
		setLoading(false);
	};

	private checkKeepRepriceInput = () => {
		const { productConfig, endorsementUpdateObj } = this.state;
		const endorsementConfiguration =
			productConfig.policyConfiguration.endorsements.find(
				(endorsement) =>
					endorsement.name === endorsementUpdateObj.endorsementName,
			);
		const keepRepriceInput =
			endorsementConfiguration && endorsementConfiguration.reprice === 'input';
		return keepRepriceInput;
	};
}

/**
 * Returns the current exposures combined with the current addExposures(type ExposureCreateRequest)
 * minus the removed exposures.
 * @param currentExposures Existing exposures on the policy
 * @returns (Exposure | ExposureCreateRequest)[]
 */
export const getDisplayedExposures = (
	currentExposures: Exposure[],
	endorsementUpdateObj: EndorsementUpdateRequest,
) => {
	const addedExposures = endorsementUpdateObj.addExposures;
	const removedExposures = endorsementUpdateObj.endExposures;
	// Locators inside endExposures only match to existing exposures.
	const currentExposuresMinusRemoved = currentExposures.filter((exp) => {
		return !removedExposures.includes((exp as Exposure).locator);
	});

	return (
		currentExposuresMinusRemoved as (Exposure | ExposureCreateRequest)[]
	).concat(addedExposures);
};

function withContext(Component: any) {
	return (props: Omit<Props, 'authData'>) => (
		<AuthContext.Consumer>
			{(authData) => (
				<ErrorContext.Consumer>
					{(errorData) => (
						<Component {...props} authData={authData} errorData={errorData} />
					)}
				</ErrorContext.Consumer>
			)}
		</AuthContext.Consumer>
	);
}

export default withContext(EndorsementProvider);
